import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import { resolveVerticalStack } from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { computeLabelButtonRow, frameButtonX } from "./tabRowLayout.js";
import { resolveOriginX } from "./directionalUi.js";
import spec from "../data/layouts/statUpgrade.json";

/** 강화 버튼 너비 — wrapWidth 산정 + StatUpgradeTabView widthOverride 공용 SSoT (뷰가 import). */
export const BUTTON_WIDTH = 88;
/** 텍스트가 버튼 좌측 경계를 침범하지 않게 두는 여백(px). */
const WRAP_MARGIN = 8;
const SCROLLBAR_GUTTER = 12;

export interface StatRowLayout {
  /** 행 배경 프레임 (스크롤 로컬 좌표, origin 0.5). */
  readonly frame: { x: number; y: number; width: number; height: number };
  /** 행 좌측 소형 스탯 아이콘. */
  readonly icon: { x: number; y: number };
  /** 스크롤 로컬 좌표 (origin은 rowTextOriginX, 세로 중앙). */
  readonly name: { x: number; y: number };
  /** 2줄 소스 분해 ("스탯+3 · 환생+5 · 유물×1.3"). */
  readonly breakdown: { x: number; y: number };
  /** 3줄 현재 최종값 → 다음 예상값. */
  readonly result: { x: number; y: number };
  readonly button: { x: number; y: number };
  /** name/breakdown/result 자동 줄바꿈 폭 — 버튼 좌측 경계까지(겹침 방지). */
  readonly wrapWidth: number;
}

export interface StatUpgradeLayout {
  /** 헤더(제목/배수) origin x. */
  readonly headTextOriginX: number;
  /** 스크롤 행 라벨/값 origin x. */
  readonly rowTextOriginX: number;
  /** 헤더 제목 영역 (origin headTextOriginX). */
  readonly title: Rect;
  /** 제목 아래 팁 캐러셀 영역 (전체폭 컨테이너) — TipCarouselView 배치 기준. */
  readonly tip: Rect;
  /** 배수 선택 영역 (좌측 고정, MultiplierSelector가 dir 내부 처리) — 높이 인지로 스크롤과 세로 침범 차단. */
  readonly multiplier: Rect;
  /** 스크롤 영역 rect (배수 아래 ~ 패널 하단 inset, 세로 스택 fill). */
  readonly scroll: Rect;
  /** 스크롤 내부 행 (로컬 좌표). */
  readonly rows: readonly StatRowLayout[];
  /** 스크롤 콘텐츠 총 높이. */
  readonly contentHeight: number;
}

/**
 * 스탯 강화 탭 좌표 — title(상단)+스크롤(fill)+배수 라디오(하단)를 resolveVerticalStack(@yjsgames/phaser-ui)으로
 * 위→아래 누적 배치해 배수 라디오와 ScrollableList 영역의 **세로 침범을 구조적으로 차단**한다
 * (fill이 배수바 공간을 남김 → scroll과 배수바 비침범). 배수 라디오는 하단 고정(엄지 친화). 스탯 항목 5종이 패널 하단 밖으로 삐져나오던 버그를
 * 스크롤로 해결한다(절대좌표 직접 배치 → 마스킹+스크롤 로컬좌표). rectsOverlap 단위테스트가 침범 회귀를 정적 검증.
 * 행 내부(스크롤 cell)는 statUpgrade.json offset + computeLabelButtonRow(RTL), wrapWidth로 버튼 가로 침범 차단.
 * @param panelBottom LAYOUT.tabPanelBottom — 스크롤 영역 하단 기준.
 */
export function computeStatUpgradeLayout(
  content: Rect,
  dir: TextDirection,
  rowCount: number,
  panelBottom: number,
): StatUpgradeLayout {
  const head = computeLabelButtonRow(content.x, content.width, 0, dir);
  // 헤더 2종(title/multiplier) + 스크롤(fill)을 세로 스택으로 누적 — 세로 겹침 구조적 차단.
  const stackBottom = panelBottom - spec.listBottomInset;
  // 배수 라디오를 하단에 고정(엄지 친화) — title 아래로 scroll(fill)이 채우고 multiplier가 맨 아래.
  const stack = resolveVerticalStack(
    {
      children: [
        // 타이틀 제거(상단 "스탯 업그레이드" 텍스트 없앰) — 높이 0으로 두면 tip이 최상단으로 올라가고
        // scroll(fill)이 남은 공간을 채워 상단 빈 공간이 사라진다(spec.titleH 무시).
        { id: "title", height: 0, gapTop: 0 },
        { id: "tip", height: spec.tipH, gapTop: spec.tipGap },
        { id: "scroll", fill: true, gapTop: spec.scrollGapTop },
        { id: "multiplier", height: spec.multiplierH, gapTop: spec.multiplierGap },
      ],
    },
    { x: content.x, y: content.y, width: content.width, height: stackBottom - content.y },
  );
  const titleRect = stack.title!;
  const tipRect = stack.tip!;
  const multiplierRect = stack.multiplier!;
  const scrollRect = stack.scroll!;

  // 버튼 x는 프레임 경계에서 파생(frameButtonX — 마진 포함) — 프레임 밖 침범이 구조적으로 불가능.
  const frameGeom = {
    x: (content.width - SCROLLBAR_GUTTER + 4) / 2,
    width: content.width - SCROLLBAR_GUTTER - 4,
  };
  const buttonX = frameButtonX(frameGeom, BUTTON_WIDTH, dir);
  const iconLx = dir === "rtl" ? content.width - spec.labelPad - 12 : spec.labelPad + 12;
  const labelLx = dir === "rtl" ? content.width - spec.labelPad - 30 : spec.labelPad + 30;
  const buttonEdge = dir === "rtl" ? buttonX + BUTTON_WIDTH / 2 : buttonX - BUTTON_WIDTH / 2;
  const wrapWidth =
    dir === "rtl"
      ? Math.max(48, labelLx - buttonEdge - WRAP_MARGIN)
      : Math.max(48, buttonEdge - labelLx - WRAP_MARGIN);

  const rows: StatRowLayout[] = [];
  for (let i = 0; i < rowCount; i++) {
    const cy = spec.rowHeight / 2 + i * spec.rowHeight;
    rows.push({
      frame: { x: frameGeom.x, y: cy, width: frameGeom.width, height: spec.rowHeight - 6 },
      icon: { x: iconLx, y: cy },
      name: { x: labelLx, y: cy + spec.nameDy },
      breakdown: { x: labelLx, y: cy + spec.breakdownDy },
      result: { x: labelLx, y: cy + spec.resultDy },
      button: { x: buttonX, y: cy },
      wrapWidth,
    });
  }

  return {
    headTextOriginX: head.textOriginX,
    rowTextOriginX: resolveOriginX(dir),
    // 텍스트 x는 head.textX(RTL 미러), y/height는 스택 결과(세로 침범 차단). 배수바는 좌측 고정(stack.x).
    title: { x: head.textX, y: titleRect.y, width: content.width, height: titleRect.height },
    tip: { x: content.x, y: tipRect.y, width: content.width, height: tipRect.height },
    multiplier: multiplierRect,
    scroll: scrollRect,
    rows,
    contentHeight: rowCount * spec.rowHeight,
  };
}
